import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription 
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../components/ui/dialog';
import { 
  Activity,
  Globe,
  Monitor,
  Clock,
  MessageSquare,
  User,
  RefreshCw,
  Loader2,
  Circle
} from 'lucide-react';
import { API_URL } from '../config/api';
import { formatDistanceToNow } from 'date-fns';
import SessionMonitor from '../components/SessionMonitor';
import SessionDetailView from '../components/SessionDetailView';

interface ActiveSession {
  sessionId: string;
  siteId: string;
  siteName: string;
  siteUrl: string;
  sessionStart: string;
  lastActivity: string;
  duration: number;
  currentPage: string;
  interactionCount: number;
  latestIntent: string;
  hasOpenReplay: boolean;
  geography: {
    country: string;
    city: string;
  };
  browser: {
    browser: string;
    os: string;
  };
  isActive: boolean;
}

export default function ActiveSessions() {
  const [sessions, setSessions] = useState<ActiveSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedSession, setSelectedSession] = useState<ActiveSession | null>(null);
  const [isMonitorOpen, setIsMonitorOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [summary, setSummary] = useState({
    active30Minutes: 0,
    active5Minutes: 0,
    withInteractions: 0
  });

  // Fetch active sessions
  const fetchActiveSessions = async () => {
    try {
      // Use relative URL for proxy to work correctly in development
      const response = await fetch(`${API_URL}/active-sessions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch active sessions');
      }
      
      const data = await response.json();
      setSessions(data.sessions);
      
      // Fetch summary
      const summaryResponse = await fetch(`${API_URL}/active-sessions/summary`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      
      if (summaryResponse.ok) {
        const summaryData = await summaryResponse.json();
        setSummary(summaryData);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching active sessions:', error);
      setLoading(false);
    }
  };

  // Initial fetch and auto-refresh
  useEffect(() => {
    fetchActiveSessions();
    
    if (autoRefresh) {
      const interval = setInterval(fetchActiveSessions, 10000); // Refresh every 10 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const openMonitor = (session: ActiveSession) => {
    setSelectedSession(session);
    setIsMonitorOpen(true);
  };

  const openDetails = (sessionId: string) => {
    setSelectedSessionId(sessionId);
    setIsDetailOpen(true);
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Active Sessions</h1>
            <p className="text-muted-foreground mt-1">
              Monitor real-time user sessions and interactions
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant={autoRefresh ? "default" : "outline"}
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${autoRefresh ? 'animate-spin' : ''}`} />
              Auto-refresh: {autoRefresh ? 'On' : 'Off'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchActiveSessions}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh Now
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium">Active Now</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{summary.active5Minutes}</div>
            <p className="text-xs text-muted-foreground">Active in last 5 minutes</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium">Recent Sessions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{summary.active30Minutes}</div>
            <p className="text-xs text-muted-foreground">Active in last 30 minutes</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium">With Interactions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{summary.withInteractions}</div>
            <p className="text-xs text-muted-foreground">Sessions with chat/events</p>
          </CardContent>
        </Card>
      </div>

      {/* Sessions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Live Sessions</CardTitle>
          <CardDescription>
            Click "Monitor" to view real-time session details
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sessions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No active sessions at the moment</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Session</TableHead>
                  <TableHead>Site</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Browser</TableHead>
                  <TableHead>Current Page</TableHead>
                  <TableHead>Activity</TableHead>
                  <TableHead>Intent</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessions.map((session) => (
                  <TableRow key={session.sessionId}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Circle 
                          className={`h-3 w-3 ${session.isActive ? 'text-green-500 fill-green-500' : 'text-yellow-500 fill-yellow-500'}`} 
                        />
                        <span className="text-xs text-muted-foreground">
                          {session.isActive ? 'Active' : 'Idle'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-mono text-sm">{session.sessionId.slice(0, 8)}...</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDuration(session.duration)}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm">{session.siteName}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {session.geography.city !== 'Unknown' ? (
                          <span>{session.geography.city}, {session.geography.country}</span>
                        ) : (
                          <span className="text-muted-foreground">Unknown</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p>{session.browser.browser}</p>
                        <p className="text-xs text-muted-foreground">{session.browser.os}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-xs">
                        <p className="text-sm truncate" title={session.currentPage}>
                          {session.currentPage ? (
                            (() => {
                              try {
                                // Handle relative URLs
                                if (session.currentPage.startsWith('/')) {
                                  return session.currentPage;
                                }
                                // Handle full URLs
                                return new URL(session.currentPage).pathname;
                              } catch {
                                // If URL parsing fails, just show the value
                                return session.currentPage;
                              }
                            })()
                          ) : 'Unknown'
                          }
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {session.interactionCount > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            <MessageSquare className="h-3 w-3 mr-1" />
                            {session.interactionCount}
                          </Badge>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(session.lastActivity), { addSuffix: true })}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {session.latestIntent && (
                        <Badge variant="outline" className="text-xs">
                          {session.latestIntent}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDetails(session.sessionId)}
                        >
                          Details
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => openMonitor(session)}
                        >
                          <Monitor className="h-4 w-4 mr-1" />
                          Monitor
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Session Monitor Dialog */}
      <Dialog open={isMonitorOpen} onOpenChange={setIsMonitorOpen}>
        <DialogContent className="max-w-[95vw] h-[95vh] p-0">
          <DialogHeader className="p-4 border-b">
            <DialogTitle>Live Session Monitor</DialogTitle>
            {selectedSession && (
              <DialogDescription>
                Monitoring {selectedSession.siteName} • Session {selectedSession.sessionId.substring(0, 12)}...
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            {selectedSession && (
              <SessionMonitor
                sessionId={selectedSession.sessionId}
                onClose={() => setIsMonitorOpen(false)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Session Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-[90vw] h-[90vh] p-0">
          <DialogHeader className="sr-only">
            <DialogTitle>Session Details</DialogTitle>
            <DialogDescription>
              Detailed information about the selected session
            </DialogDescription>
          </DialogHeader>
          <div className="h-full">
            {selectedSessionId && (
              <SessionDetailView
                sessionId={selectedSessionId}
                onClose={() => setIsDetailOpen(false)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}